import { normalizeBlockMathDelimiters } from '../../components/utils/contentConverter';

const INCOMPLETE_IMAGE_MARKDOWN_REGEX = /(?<!\\)!\[[^\]\n]*(?:\](?:\[[^\]\n]*|\([^)\n]*)?)?$/;

function isInsideOpenCodeContext(prefix: string): boolean {
  const fenceMatches = prefix.match(/```/g);
  if (fenceMatches && fenceMatches.length % 2 === 1) return true;

  const lastLineStart = prefix.lastIndexOf('\n') + 1;
  const lastLine = prefix.slice(lastLineStart);
  let inlineBacktickCount = 0;
  for (let i = 0; i < lastLine.length; i++) {
    if (lastLine[i] === '`' && (i === 0 || lastLine[i - 1] !== '\\')) inlineBacktickCount++;
  }
  return inlineBacktickCount % 2 === 1;
}

function findIncompleteTrailingImageIndex(text: string): number {
  const match = text.match(INCOMPLETE_IMAGE_MARKDOWN_REGEX);
  if (!match) return -1;
  const matchIndex = match.index ?? 0;
  if (isInsideOpenCodeContext(text.slice(0, matchIndex))) return -1;
  return matchIndex;
}

/**
 * True when `text` ends with an unfinished markdown image (e.g. `![alt`,
 * `![alt](http`). Skips matches inside open code fences or inline code spans.
 * Callers use this to decide whether to show an image-generation indicator
 * while the markdown is mid-stream.
 */
export function hasIncompleteTrailingImageMarkdown(text: string): boolean {
  return findIncompleteTrailingImageIndex(text) !== -1;
}

/**
 * Preprocesses markdown text for rendering with ReactMarkdown.
 * Adds trailing spaces for line breaks (except inside math blocks)
 * and normalizes block math delimiters.
 *
 * When `isStreaming` is true, a trailing incomplete image markdown fragment
 * is stripped so the raw syntax isn't visible to the user mid-stream. A
 * loading indicator should be shown separately by the caller (see
 * `hasIncompleteTrailingImageMarkdown`). Completed `![alt](url)` is
 * untouched and rendered as a real image.
 *
 * @param text - The markdown text to preprocess
 * @param isStreaming - Whether the text is currently being streamed
 * @returns Preprocessed markdown text ready for ReactMarkdown
 */
export function preprocessMarkdownText(text: string, isStreaming = false): string {
  let source = text;
  if (isStreaming) {
    const index = findIncompleteTrailingImageIndex(text);
    if (index !== -1) source = text.slice(0, index);
  }
  // Normalize $$ to be on their own lines so remark-math can parse block math
  const normalized = normalizeBlockMathDelimiters(source);
  const lines = normalized.split('\n');
  let inMathBlock = false;

  return lines.map((line) => {
    const trimmed = line.trim();
    if (trimmed === '$$') {
      inMathBlock = !inMathBlock;
      return line;
    }
    // Add trailing spaces for markdown line breaks, but not inside math blocks
    return inMathBlock ? line : line + '  ';
  }).join('\n');
}
